import { BRIDGE_SEATS, type BridgeNtState, type BridgeSeat } from '../games/bridge-nt';
import type { Card } from '../games/heads-up-poker';

type PublicMove = { mv: unknown };

/** Reconstruct a public, unfinished Bridge trace without reading private deals.
 * The server has already checked ownership/follow suit. Public archives omit
 * ProvideHands, so ordinary engine init/apply cannot replay their Play moves.
 */
export function bridgePublicReplay(
  finalState: BridgeNtState,
  history: readonly PublicMove[],
  upToPly: number,
): BridgeNtState {
  const state: BridgeNtState = {
    phase: finalState.phase === 'awaiting_hands' ? 'awaiting_hands' : 'play',
    opening_leader: finalState.opening_leader,
    provided: [...finalState.provided],
    hands: [null, null, null, null],
    completed: [],
    current: { leader: finalState.opening_leader, cards: [] },
    tricks_won: [0, 0],
    outcome: null,
  };
  for (const entry of history.slice(0, Math.max(0, upToPly))) {
    if (typeof entry.mv !== 'object' || entry.mv === null || !('play' in entry.mv)) continue;
    const card = (entry.mv as { play: { card: Card } }).play.card;
    if (!card || !Number.isInteger(card.suit) || card.suit < 0 || card.suit > 3 ||
      !Number.isInteger(card.rank) || card.rank < 2 || card.rank > 14) {
      throw new Error('Invalid public Bridge card');
    }
    state.phase = 'play';
    state.current.cards.push({ suit: card.suit, rank: card.rank });
    if (state.current.cards.length !== 4) continue;
    const { leader, cards } = state.current;
    const suit = cards[0].suit;
    let offset = 0;
    for (let i = 1; i < cards.length; i++) {
      if (cards[i].suit === suit && cards[i].rank > cards[offset].rank) offset = i;
    }
    const winner: BridgeSeat = BRIDGE_SEATS[(BRIDGE_SEATS.indexOf(leader) + offset) % 4];
    state.completed.push({ leader, cards, winner });
    state.tricks_won[BRIDGE_SEATS.indexOf(winner) % 2]++;
    state.current = { leader: winner, cards: [] };
  }
  return state;
}
